import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Area, AreaChart } from 'recharts';
import { Calendar, TrendingDown, TrendingUp, Clock, Plus, Download, Share2, AlertCircle } from 'lucide-react';
import { format, differenceInDays, addDays } from 'date-fns';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TimelineItem = ({ date, dayNumber, isActive, isCompleted, onClick }) => (
  <div 
    className={`timeline-item cursor-pointer transition-all ${
      isActive ? 'active bg-blue-100 border-blue-500' : 
      isCompleted ? 'bg-green-100 border-green-500' : 'bg-gray-100 border-gray-300'
    } border-2 rounded-lg p-3 text-center min-w-[80px]`}
    onClick={onClick}
  >
    <div className="font-medium text-sm">{format(new Date(date), 'MMM dd')}</div>
    <div className="text-xs text-gray-600">Day {dayNumber}</div>
  </div>
);

const ComparisonCard = ({ title, value, unit, change, isPositive }) => (
  <Card>
    <CardContent className="p-4 text-center">
      <div className={`text-2xl font-bold mb-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {change && (change > 0 ? '+' : '')}{value}{unit}
      </div>
      <div className="text-sm text-gray-600 mb-2">{title}</div>
      {change !== undefined && (
        <div className={`flex items-center justify-center text-xs ${
          (isPositive && change > 0) || (!isPositive && change < 0) ? 'text-green-600' : 'text-red-600'
        }`}>
          {(isPositive && change > 0) || (!isPositive && change < 0) ? 
            <TrendingUp className="w-3 h-3 mr-1" /> : 
            <TrendingDown className="w-3 h-3 mr-1" />
          }
          {Math.abs(change).toFixed(1)}% vs baseline
        </div>
      )}
    </CardContent>
  </Card>
);

const TemporalView = ({ sessionData, currentAnalysis, onComplete }) => {
  const [patientHistory, setPatientHistory] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [healingData, setHealingData] = useState([]);
  const [volumeData, setVolumeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [comparisonMetrics, setComparisonMetrics] = useState({});

  useEffect(() => {
    if (sessionData?.patient_id) {
      loadPatientHistory();
    }
  }, [sessionData]);

  const loadPatientHistory = async () => {
    setLoading(true);
    try {
      // In a real app, this would load actual patient history
      // For demo, we'll generate synthetic historical data
      const syntheticHistory = generateSyntheticHistory(sessionData);
      setPatientHistory(syntheticHistory);
      
      // Generate healing progression data
      const healingProgression = generateHealingProgression(syntheticHistory, currentAnalysis);
      setHealingData(healingProgression.healing);
      setVolumeData(healingProgression.volume);
      
      // Calculate comparison metrics
      const metrics = calculateComparisonMetrics(syntheticHistory, currentAnalysis);
      setComparisonMetrics(metrics);
      
    } catch (error) {
      console.error('Failed to load patient history:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSyntheticHistory = (session) => {
    const baseDate = new Date(session.injury_date || Date.now());
    const currentMetrics = currentAnalysis?.metrics || {};
    
    // Generate 4 historical assessments
    const history = [];
    for (let i = 0; i < 4; i++) {
      const assessmentDate = addDays(baseDate, i * 7);
      const dayNumber = i * 7;
      
      // Simulate healing progression (gradual improvement)
      const healingFactor = 1 - (i * 0.2); // 20% improvement each week
      const noiseFactor = 0.9 + Math.random() * 0.2; // Add some realistic variation
      
      history.push({
        id: `${session.id}_${i}`,
        session_id: session.id,
        patient_id: session.patient_id,
        assessment_date: assessmentDate.toISOString(),
        day_number: dayNumber,
        metrics: {
          surface_area_cm2: (currentMetrics.surface_area_cm2 || 5.0) * healingFactor * noiseFactor,
          volume_cm3: (currentMetrics.volume_cm3 || 1.0) * healingFactor * noiseFactor,
          max_depth_mm: (currentMetrics.max_depth_mm || 3.0) * healingFactor * noiseFactor,
          avg_depth_mm: (currentMetrics.avg_depth_mm || 2.0) * healingFactor * noiseFactor,
          healing_stage: i < 2 ? 'Inflammatory' : i < 3 ? 'Proliferative' : 'Maturation',
          confidence: 85 + i * 2
        },
        is_baseline: i === 0,
        is_current: i === 3
      });
    }
    
    return history;
  };

  const generateHealingProgression = (history, current) => {
    const healing = history.map(assessment => ({
      date: format(new Date(assessment.assessment_date), 'MMM dd'),
      day: assessment.day_number,
      surfaceArea: parseFloat(assessment.metrics.surface_area_cm2),
      volume: parseFloat(assessment.metrics.volume_cm3),
      maxDepth: parseFloat(assessment.metrics.max_depth_mm),
      confidence: assessment.metrics.confidence
    }));

    const volume = history.map(assessment => ({
      date: format(new Date(assessment.assessment_date), 'MMM dd'),
      day: assessment.day_number,
      volume: parseFloat(assessment.metrics.volume_cm3),
      surfaceArea: parseFloat(assessment.metrics.surface_area_cm2)
    }));

    return { healing, volume };
  };

  const calculateComparisonMetrics = (history, current) => {
    if (history.length < 2) return {};
    
    const baseline = history[0];
    const latest = history[history.length - 1];
    
    const areaReduction = ((baseline.metrics.surface_area_cm2 - latest.metrics.surface_area_cm2) / baseline.metrics.surface_area_cm2) * 100;
    const volumeReduction = ((baseline.metrics.volume_cm3 - latest.metrics.volume_cm3) / baseline.metrics.volume_cm3) * 100;
    
    // Calculate healing rate (area reduction per day)
    const totalDays = history[history.length - 1].day_number;
    const healingRate = areaReduction / totalDays;
    
    // Estimate recovery time
    const remainingArea = latest.metrics.surface_area_cm2;
    const estimatedDays = healingRate > 0 ? Math.ceil(remainingArea / (baseline.metrics.surface_area_cm2 * healingRate / 100)) : 0;
    
    return {
      areaReduction: areaReduction,
      volumeReduction: volumeReduction,
      healingRate: healingRate,
      estimatedRecovery: estimatedDays
    };
  };

  const addNewAssessment = () => {
    // In real app, this would navigate to a new session creation
    alert('In a production system, this would allow adding a new assessment session for the same patient.');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600">Loading temporal analysis...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-blue-600" />
            Temporal Healing Tracking
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-gray-600">Patient ID: <span className="font-mono font-medium">{sessionData?.patient_id}</span></p>
              <p className="text-sm text-gray-500">Tracking healing progression over time</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={loadPatientHistory}>
                <Clock className="w-4 h-4 mr-2" />
                Refresh History
              </Button>
              <Button onClick={addNewAssessment}>
                <Plus className="w-4 h-4 mr-2" />
                Add Assessment
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timeline */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Assessment Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-300 -translate-y-1/2"></div>
            <div className="flex justify-between items-center relative z-10">
              {patientHistory.map((assessment, index) => (
                <TimelineItem
                  key={assessment.id}
                  date={assessment.assessment_date}
                  dayNumber={assessment.day_number}
                  isActive={assessment.is_current}
                  isCompleted={index < patientHistory.length - 1}
                  onClick={() => setSelectedSession(assessment)}
                />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Healing Progression Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Healing Progression</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={healingData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="surfaceArea" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="Surface Area (cm²)" 
                />
                <Line 
                  type="monotone" 
                  dataKey="maxDepth" 
                  stroke="#f97316" 
                  strokeWidth={2}
                  name="Max Depth (mm)" 
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Volume Reduction Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Volume Reduction</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={volumeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar 
                  dataKey="volume" 
                  fill="#3b82f6" 
                  name="Volume (cm³)"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Comparison Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Healing Progress Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <ComparisonCard
              title="Area Reduction since baseline"
              value={comparisonMetrics.areaReduction?.toFixed(1) || '0'}
              unit="%"
              change={comparisonMetrics.areaReduction}
              isPositive={false} // Reduction is good
            />
            <ComparisonCard
              title="Healing Rate"
              value={comparisonMetrics.healingRate?.toFixed(1) || '0'}
              unit=" mm²/day"
              isPositive={true}
            />
            <ComparisonCard
              title="Est. Recovery Time"
              value={comparisonMetrics.estimatedRecovery || '0'}
              unit=" days"
              isPositive={false}
            />
          </div>
        </CardContent>
      </Card>

      {/* Side-by-side Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Visual Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-3 text-center">Baseline (Day 0)</h4>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                <div className="text-center text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Baseline image would appear here</p>
                  <p className="text-xs">Surface: {patientHistory[0]?.metrics?.surface_area_cm2?.toFixed(1)} cm²</p>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-medium mb-3 text-center">Current (Day {patientHistory[patientHistory.length - 1]?.day_number || 0})</h4>
              <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center border-2 border-dashed border-gray-300">
                <div className="text-center text-gray-500">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Current image would appear here</p>
                  <p className="text-xs">Surface: {currentAnalysis?.metrics?.surface_area_cm2} cm²</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Treatment & Management Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-600" />
            Treatment & Management Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Treatment Management */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4 border-b pb-2">Treatment / Management</h4>
            <div className="space-y-3 text-sm">
              <div>
                <span className="font-medium">• Wound Care:</span> 
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.wound_care || 
                   "(cleansing, debridement, dressings used) Daily wound cleansing with saline, gentle debridement of loose tissue, apply silver sulfadiazine cream, cover with non-adherent dressing"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Pain Control:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.pain_control || 
                   "Acetaminophen 1000mg q6h, ibuprofen 600mg q8h for inflammation, topical lidocaine gel PRN"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Fluids:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.fluid_resuscitation || 
                   "Maintain adequate hydration, monitor for signs of dehydration especially with larger burns"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Antibiotics:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.antibiotics || 
                   "Topical silver sulfadiazine, systemic antibiotics only if signs of infection develop"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Tetanus:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.tetanus || 
                   "Verify tetanus immunization status, administer booster if >5-10 years since last dose"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Special Procedures:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.special_procedures || 
                   "(escharotomy, grafting, etc.) Monitor for compartment syndrome, consider dermatology/plastic surgery consultation for deep burns"}
                </span>
              </div>
              <div>
                <span className="font-medium">• Patient Education:</span>
                <span className="ml-2 text-gray-700">
                  {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_management?.patient_education || 
                   "(wound care, infection signs, home support) Daily dressing changes, signs of infection (increased redness, warmth, purulent drainage), return instructions"}
                </span>
              </div>
            </div>
          </div>

          {/* Clinical Assessment */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4 border-b pb-2">Assessment</h4>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-700 leading-relaxed">
                {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.clinical_assessment?.diagnosis || 
                 "Burn injury classification and diagnosis based on clinical findings and 3D analysis"}
              </p>
              <div className="mt-3 space-y-2">
                <p className="text-sm">
                  <span className="font-medium">Key Findings:</span> 
                  <span className="ml-2">
                    {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.clinical_assessment?.key_findings || 
                     "Burn assessment completed with comprehensive evaluation of depth, area, and characteristics"}
                  </span>
                </p>
                <p className="text-sm">
                  <span className="font-medium">TBSA & Depth Summary:</span>
                  <span className="ml-2">
                    {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.clinical_assessment?.tbsa_depth_summary || 
                     `TBSA: ${currentAnalysis?.metrics?.tbsa_percentage || 'TBD'}%, Depth: ${currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.BurnClassification ? 
                       Object.keys(currentAnalysis.advancedAnalysisResults.quantitative.result.analysis_data.BurnClassification).join(', ') : 'Mixed depth'}`}
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Treatment Plan */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4 border-b pb-2">Plan</h4>
            <div className="space-y-3 text-sm">
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>Continue wound care as outlined above</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>Monitor for infection and healing progression</span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>
                  <span className="font-medium">Referral to:</span> 
                  <span className="ml-2">
                    {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_plan?.referrals || 
                     "(physiotherapy / occupational therapy / nutrition / psychology / other) Physical therapy for mobility, occupational therapy for functional assessment"}
                  </span>
                </span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>
                  <span className="font-medium">Arrange follow-up:</span>
                  <span className="ml-2">
                    {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_plan?.follow_up || 
                     "7-10 days for dressing change and wound assessment, sooner if signs of infection"}
                  </span>
                </span>
              </div>
              <div className="flex items-start">
                <span className="font-medium mr-2">•</span>
                <span>
                  <span className="font-medium">Discharge/Admission considerations:</span>
                  <span className="ml-2">
                    {currentAnalysis?.advancedAnalysisResults?.quantitative?.result?.analysis_data?.treatment_plan?.discharge_planning || 
                     "Outpatient management appropriate, admission criteria include TBSA >10%, involvement of face/hands/genitals, inhalation injury"}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Follow-up */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-4 border-b pb-2">Follow-Up</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Next Appointment:</span>
                <div className="mt-2 p-3 bg-blue-50 rounded border">
                  <span className="text-gray-700">
                    {format(addDays(new Date(), 7), 'EEEE, MMMM do, yyyy')} (7 days)
                  </span>
                </div>
              </div>
              <div>
                <span className="font-medium">Location:</span>
                <div className="mt-2 p-3 bg-blue-50 rounded border">
                  <span className="text-gray-700">Burn Clinic / Wound Care Center</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="flex justify-between items-center p-6">
          <div>
            <h4 className="font-medium mb-1">Treatment Planning Complete</h4>
            <p className="text-sm text-gray-600">Comprehensive treatment recommendations and follow-up plan established</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => alert('Export treatment plan')}>
              <Download className="w-4 h-4 mr-2" />
              Export Plan
            </Button>
            <Button variant="outline" onClick={() => alert('Share with care team')}>
              <Share2 className="w-4 h-4 mr-2" />
              Share Plan
            </Button>
            <Button onClick={() => onComplete && onComplete('report')}>
              Generate Final Report
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TemporalView;